import { useState } from 'react';
import { Link, useSearch } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import {
  fetchPortalCampaignProgress,
  fetchPortalCampaignReportDownload,
  fetchPortalCampaignWorkspace,
} from '@/lib/portalApi';
import type { PortalContextResponse } from '@shared/portal-api-types';
import { PortalShell } from './PortalShell';
import {
  PortalErrorBlock,
  PortalLoadingBlock,
  portalCardClass,
  portalLinkClass,
  portalPageSubtitleClass,
  portalPrimaryButtonClass,
  portalProgressFillClass,
  portalProgressTrackClass,
  portalTableClass,
  portalTableHeadClass,
} from './portalUi';
import { buildPortalHref, buildPortalSectionHref } from './portalCycleQuery';

type WorkspaceTab = 'overview' | 'sitelist' | 'compliance' | 'instructions' | 'reports';

const tabs: Array<{ id: WorkspaceTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'sitelist', label: 'Sitelist' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'instructions', label: 'Posting instructions' },
  { id: 'reports', label: 'Retailer reports' },
];

function dateLabel(value: string | null): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })
    .format(new Date(value));
}

function campaignTypeLabel(value: 'pack' | 'manual' | 'pack_manual'): string {
  if (value === 'manual') return 'Manual campaign';
  if (value === 'pack_manual') return 'Grouped campaign';
  return 'Pack campaign';
}

function StatCard({ label, value, detail }: { label: string; value: string | number; detail?: string }) {
  return (
    <div className={portalCardClass + ' p-4'}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
      {detail ? <p className="mt-1 text-sm text-muted-foreground">{detail}</p> : null}
    </div>
  );
}

export function PortalCampaignWorkspacePage({
  context,
  campaignId,
  cycleId,
}: {
  context: PortalContextResponse;
  campaignId: number;
  cycleId: number;
}) {
  const urlSearch = useSearch();
  const [tab, setTab] = useState<WorkspaceTab>('overview');
  const [downloadingReportId, setDownloadingReportId] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const workspaceQuery = useQuery({
    queryKey: ['portal', 'campaign-workspace', campaignId, cycleId],
    queryFn: () => fetchPortalCampaignWorkspace(campaignId, cycleId),
  });
  const progressQuery = useQuery({
    queryKey: ['portal', 'progress', campaignId, cycleId],
    queryFn: () => fetchPortalCampaignProgress(campaignId, cycleId),
  });

  if (workspaceQuery.isLoading || progressQuery.isLoading) {
    return <PortalShell context={context}><PortalLoadingBlock /></PortalShell>;
  }
  if (workspaceQuery.isError || !workspaceQuery.data) {
    return (
      <PortalShell context={context}>
        <PortalErrorBlock message={workspaceQuery.error instanceof Error
          ? workspaceQuery.error.message
          : 'Could not load campaign details.'} />
      </PortalShell>
    );
  }

  const workspace = workspaceQuery.data;
  const progress = progressQuery.data;

  const downloadReport = async (reportId: number) => {
    setDownloadingReportId(reportId);
    setDownloadError(null);
    try {
      const download = await fetchPortalCampaignReportDownload(campaignId, reportId);
      const anchor = document.createElement('a');
      anchor.href = download.url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.click();
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : 'Could not download this report.');
    } finally {
      setDownloadingReportId(null);
    }
  };

  return (
    <PortalShell context={context}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Link href={buildPortalSectionHref('/portal', urlSearch)} className={portalLinkClass}>← Back to campaigns</Link>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-semibold text-foreground">{workspace.campaign.name}</h2>
              {workspace.campaign.status ? (
                <span className="rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-medium text-foreground">
                  {workspace.campaign.status}
                </span>
              ) : null}
            </div>
            <p className={portalPageSubtitleClass}>
              {workspace.retailer.name} · {workspace.cycle.name} · {dateLabel(workspace.cycle.startDate)}–{dateLabel(workspace.cycle.endDate)}
            </p>
          </div>
          <Link
            href={buildPortalHref('/portal/gallery', urlSearch, { cycleId, campaignId })}
            className={portalPrimaryButtonClass}
          >
            Campaign gallery
          </Link>
        </div>

        <nav className="flex max-w-full gap-1 overflow-x-auto rounded-lg border border-border bg-muted p-1" aria-label="Campaign sections">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                tab === item.id
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {tab === 'overview' ? (
          <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
              <StatCard label="Stores" value={workspace.summary.storeCount} />
              <StatCard label="Groups" value={workspace.summary.groupCount} />
              <StatCard label="Formats" value={workspace.summary.formatCount} />
              <StatCard label="Units due" value={workspace.summary.expectedQuantity} />
              <StatCard
                label="Stores complete"
                value={progress ? `${progress.completedStoreCount}/${progress.storeCount}` : '—'}
                detail={progress ? `${progress.completionPercent}% complete` : undefined}
              />
            </div>

            <section className={portalCardClass + ' p-5'}>
              <h3 className="text-lg font-semibold text-foreground">Campaign details</h3>
              <dl className="mt-4 grid gap-x-8 gap-y-4 text-sm md:grid-cols-2">
                <div><dt className="text-muted-foreground">Campaign type</dt><dd className="mt-1 font-medium">{campaignTypeLabel(workspace.campaign.campaignType)}</dd></div>
                <div><dt className="text-muted-foreground">Created</dt><dd className="mt-1 font-medium">{dateLabel(workspace.campaign.createdAt)}</dd></div>
                <div><dt className="text-muted-foreground">Cycle</dt><dd className="mt-1 font-medium">{workspace.cycle.name}</dd></div>
                <div><dt className="text-muted-foreground">Retailer</dt><dd className="mt-1 font-medium">{workspace.retailer.name}</dd></div>
                {workspace.campaign.description ? (
                  <div className="md:col-span-2"><dt className="text-muted-foreground">Description</dt><dd className="mt-1 whitespace-pre-wrap text-foreground">{workspace.campaign.description}</dd></div>
                ) : null}
              </dl>
            </section>

            <div className="grid gap-4 lg:grid-cols-2">
              <section className={portalCardClass + ' p-5'}>
                <h3 className="text-lg font-semibold text-foreground">Groups</h3>
                <div className="mt-3 divide-y divide-border">
                  {workspace.groups.map((group) => (
                    <div key={group.groupKey} className="flex items-center justify-between py-3 text-sm">
                      <span className="font-medium">{group.groupName}</span>
                      <span className="text-muted-foreground">{group.storeCount} store{group.storeCount === 1 ? '' : 's'}</span>
                    </div>
                  ))}
                  {workspace.groups.length === 0 ? <p className="py-4 text-sm text-muted-foreground">No group breakdown is available.</p> : null}
                </div>
              </section>
              <section className={portalCardClass + ' p-5'}>
                <h3 className="text-lg font-semibold text-foreground">Formats</h3>
                <div className="mt-3 divide-y divide-border">
                  {workspace.formats.map((format) => (
                    <div key={format.formatKey} className="flex items-center justify-between gap-4 py-3 text-sm">
                      <span className="font-medium">{format.formatName}</span>
                      <span className="text-muted-foreground">{format.installedQuantity}/{format.expectedQuantity} counted as installed{(format.unconfirmedQuantity ?? 0) > 0 && <span className="block text-amber-700">{format.unconfirmedQuantity} unconfirmed — left with manager</span>}</span>
                    </div>
                  ))}
                  {workspace.formats.length === 0 ? <p className="py-4 text-sm text-muted-foreground">No format requirements are available.</p> : null}
                </div>
              </section>
            </div>
          </div>
        ) : null}

        {tab === 'sitelist' ? (
          <div className={portalCardClass + ' overflow-x-auto'}>
            <table className={portalTableClass}>
              <thead className={portalTableHeadClass}>
                <tr><th className="px-4 py-3">Store</th><th className="px-4 py-3">Number</th><th className="px-4 py-3">Address</th><th className="px-4 py-3">Group</th><th className="px-4 py-3">Formats due</th></tr>
              </thead>
              <tbody>
                {workspace.stores.map((store) => (
                  <tr key={store.storeId} className="border-t border-border align-top">
                    <td className="px-4 py-3 font-medium">{store.storeName}</td>
                    <td className="px-4 py-3 text-muted-foreground">{store.storeNumber ?? '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{store.address ?? '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{store.groupNames.join(', ') || '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{store.formats.map((format) => `${format.formatName} × ${format.expectedQuantity}`).join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {tab === 'compliance' ? (
          <div className="space-y-4">
            {progressQuery.isError ? <PortalErrorBlock message="Campaign progress could not be loaded." /> : null}
            <div className={portalCardClass + ' overflow-x-auto'}>
              <table className={portalTableClass}>
                <thead className={portalTableHeadClass}>
                  <tr><th className="px-4 py-3">Store</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Installation</th><th className="px-4 py-3">Evidence</th></tr>
                </thead>
                <tbody>
                  {workspace.stores.map((store) => {
                    const storeProgress = progress?.stores.find((entry) => entry.storeId === store.storeId);
                    const expected = store.formats.reduce((sum, format) => sum + format.expectedQuantity, 0);
                    const installed = store.formats.reduce((sum, format) => sum + format.installedQuantity, 0);
                    const unconfirmed = store.formats.reduce((sum, format) => sum + (format.unconfirmedQuantity ?? 0), 0);
                    return (
                      <tr key={store.storeId} className="border-t border-border">
                        <td className="px-4 py-3"><p className="font-medium">{store.storeName}</p><p className="text-xs text-muted-foreground">{store.storeNumber ?? ''}</p></td>
                        <td className="px-4 py-3 capitalize">{storeProgress?.status.replace('_', ' ') ?? 'Not started'}</td>
                        <td className="px-4 py-3">
                          <div className="flex min-w-[180px] items-center gap-2">
                            <div className={portalProgressTrackClass}><div className={portalProgressFillClass} style={{ width: `${expected > 0 ? Math.min(100, Math.round((installed / expected) * 100)) : 0}%` }} /></div>
                            <span className="whitespace-nowrap text-sm">{installed}/{expected}{unconfirmed > 0 && <span className="block text-xs text-amber-700">{unconfirmed} unconfirmed — left with manager</span>}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3"><Link href={buildPortalHref('/portal/gallery', urlSearch, { cycleId, campaignId, storeId: store.storeId })} className={portalLinkClass}>Campaign gallery</Link></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {tab === 'instructions' ? (
          <div className="grid gap-4 lg:grid-cols-2">
            {workspace.instructions.map((instruction, index) => (
              <article key={`${instruction.scopeType}-${instruction.scopeName}-${index}`} className={portalCardClass + ' overflow-hidden'}>
                {instruction.imageUrl ? <img src={instruction.imageUrl} alt="Posting instruction reference" className="max-h-72 w-full object-contain bg-muted" /> : null}
                <div className="space-y-3 p-5">
                  <div><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Applies to</p><h3 className="mt-1 text-lg font-semibold">{instruction.scopeName}</h3></div>
                  {instruction.locationName ? <p className="text-sm"><span className="text-muted-foreground">In-store location:</span> {instruction.locationName}</p> : null}
                  <p className="whitespace-pre-wrap text-sm text-foreground">{instruction.text}</p>
                </div>
              </article>
            ))}
            {workspace.instructions.length === 0 ? <p className={portalCardClass + ' p-8 text-center text-muted-foreground lg:col-span-2'}>No posting instructions have been added for this campaign.</p> : null}
          </div>
        ) : null}

        {tab === 'reports' ? (
          <section className={portalCardClass}>
            {downloadError ? <div className="m-4 rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{downloadError}</div> : null}
            <div className="divide-y divide-border">
              {workspace.reports.map((report) => (
                <div key={report.reportId} className="flex flex-wrap items-center justify-between gap-4 p-5">
                  <div><h3 className="font-semibold">{report.name}</h3><p className="mt-1 text-sm text-muted-foreground">{report.status === 'SENT' ? 'Sent' : 'Approved'} · {report.evidenceImageCount} evidence image{report.evidenceImageCount === 1 ? '' : 's'} · {dateLabel(report.generatedAt)}</p></div>
                  <button type="button" className={portalPrimaryButtonClass} disabled={downloadingReportId === report.reportId} onClick={() => void downloadReport(report.reportId)}>{downloadingReportId === report.reportId ? 'Preparing…' : 'Download'}</button>
                </div>
              ))}
              {workspace.reports.length === 0 ? <p className="p-8 text-center text-muted-foreground">No approved retailer reports are available yet.</p> : null}
            </div>
          </section>
        ) : null}
      </div>
    </PortalShell>
  );
}
